import { useEffect } from 'react';
import { useStoreon } from 'storeon/react';
import { IEvents, IState } from 'src/store';

import {
  gambrel,
  hip,
  tented,
  multipitch,
  gable,
  materialsCalculations
} from "src/utils/formulas/roof";
import {IProduct} from "../../../api";

export const usePresenter = () => {
  const { dispatch, appStore, roofStore } = useStoreon<IState, IEvents>('appStore', 'roofStore');

  useEffect(() => {
    const L_kr = roofStore.L_krValue || 0;
    const L_k = roofStore.L_kValue || 0;
    const B_kr = roofStore.B_krValue || 0;
    const B_kr1 = roofStore.B_kr1Value || 0;
    const B_kr2 = roofStore.B_kr2Value || 0;
    const H_p1 = roofStore.H_p1Value || 0;
    const H_p2 = roofStore.H_p2Value || 0;
    const L_sk1 = roofStore.L_sk1Value || 0;
    const L_sk2 = roofStore.L_sk2Value || 0;

    // Найти коэффициенты профиля из прайс-листа
    const profileProduct = appStore.priceList.find((p: IProduct) => {
      const matchGroup = p.offer_group === roofStore.profileType;
      const matchType = roofStore.profileType === 'Профнастил'
        ? p.type === roofStore.pailingName
        : p.type === roofStore.metalTileName;
      return matchGroup && matchType && p.area_coeff;
    });
    const sheet_width = profileProduct?.sheet_width ?? 0;
    const area_coeff = profileProduct?.area_coeff ?? 1;

    // Для Hip/Tented — area_coeff из variables
    const hipAreaCoeff = parseFloat(appStore.variables?.hip_area_coeff) || 1.34;
    const tentedAreaCoeff = parseFloat(appStore.variables?.tented_area_coeff) || 1.37;

    let S_kr = 0, L_kon = 0, L_end = 0;
    let NN_kon = 0, NN_karn = 0, NN_tor = 0, NN_sa35 = 0, NN_sa70 = 0;

    switch (roofStore.roofType) {
      case 'gambrel':
        S_kr = gambrel.S_kr({ L_kr, B_kr1, B_kr2, H_p1, H_p2, sheet_width, area_coeff });
        L_kon = gambrel.L_kon({ L_kr });
        NN_kon = gambrel.N_kon({ L_kr });
        NN_karn = gambrel.N_karn({ L_kr });
        NN_tor = gambrel.N_tor({ B_kr1, B_kr2, H_p1, H_p2 });
        break;
      case 'hip':
        S_kr = hip.S_kr({ L_kr, B_kr, H_p1, H_p2, L_k, area_coeff: hipAreaCoeff });
        NN_kon = hip.N_kon({ L_k, L_kr, H_p1 });
        NN_karn = hip.N_karn({ L_kr, B_kr });
        NN_sa35 = hip.NN_sa35({ S_kr });
        NN_sa70 = hip.NN_sa70({ L_kon: NN_kon, L_karn: NN_karn });
        break;
      case 'tented':
        S_kr = tented.S_kr({ L_kr, B_kr, H_p1, H_p2, area_coeff: tentedAreaCoeff });
        NN_kon = tented.N_kon({ L_kr, H_p1 });
        NN_karn = tented.N_karn({ L_kr, B_kr });
        NN_sa35 = tented.NN_sa35({ S_kr });
        NN_sa70 = tented.NN_sa70({ L_kon: NN_kon, L_karn: NN_karn });
        break;
      case 'multipitch':
        S_kr = multipitch.S_kr({ B_kr, B_kr2, L_k, L_kr, H_p1 });
        L_kon = multipitch.L_kon({ L_kr, L_k });
        NN_kon = multipitch.L_kon({ L_kr, L_k });
        NN_karn = multipitch.L_karn({ L_kr, B_kr2 });
        NN_tor = multipitch.L_tor({ B_kr, H_p1 });
        L_end = multipitch.L_end({ B_kr, B_kr2, H_p1 });
        break;
      case 'gable':
        S_kr = gable.S_kr({ L_kr, L_sk1, L_sk2, sheet_width, area_coeff });
        L_kon = gable.L_kon({ L_kr });
        NN_kon = gable.N_kon({ L_kr });
        NN_karn = gable.N_karn({ L_kr });
        NN_tor = gable.N_tor({ L_sk1, L_sk2 });
        break;
      default:
        console.log('roofType is unknown!');
    }

    const roofCalculations = {
      dimensions: { S_kr, L_kon, L_karn: 0, L_tor: 0, L_end },
      materials: materialsCalculations({ S_kr, L_kon, L_end, NN_kon, NN_karn, NN_tor, NN_sa35, NN_sa70 })
    };

    dispatch('roof/set-roof-area', { roofArea: S_kr });
    dispatch('roof/set-roof-calculations', { roofCalculations });
  }, [
    dispatch,
    appStore.priceList,
    appStore.variables,
    roofStore.L_krValue,
    roofStore.L_kValue,
    roofStore.B_krValue,
    roofStore.B_kr1Value,
    roofStore.B_kr2Value,
    roofStore.H_p1Value,
    roofStore.H_p2Value,
    roofStore.L_sk1Value,
    roofStore.L_sk2Value,
    roofStore.roofType,
    roofStore.profileType,
    roofStore.metalTileName,
    roofStore.pailingName,
  ]);

  return {
    roofType: roofStore.roofType,
    roofArea: roofStore.roofArea,
    roofCalculations: roofStore.roofCalculations,

    profileType: roofStore.profileType,
    metalTileName: roofStore.metalTileName,
    pailingName: roofStore.pailingName,

    coatingName: roofStore.coatingName,
    thicknessName: roofStore.thicknessName,

    priceList:  appStore.priceList,
    productRoof: appStore.priceList
      .filter((product: IProduct) => product.offer_group === roofStore.profileType)
      .filter((product: IProduct) => (roofStore.profileType === 'Профнастил' ? product.type === roofStore.pailingName : product.type === roofStore.metalTileName))
      .filter((product: IProduct) => product.coating === roofStore.coatingName)
      .find((product: IProduct) => product.thickness === roofStore.thicknessName),
    productRidgeCap: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Доборные элементы')
      .filter((product: IProduct) => product.coating === roofStore.coatingName)
      .find((product: IProduct) => product.type === 'Планка конька плоская 150х150 2 метра'),
    productCornicePlank: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Доборные элементы')
      .filter((product: IProduct) => product.coating === roofStore.coatingName)
      .find((product: IProduct) => product.type === 'Карнизная планка 2 метра'),
    productEndStrap: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Доборные элементы')
      .filter((product: IProduct) => product.coating === roofStore.coatingName)
      .find((product: IProduct) => product.type === 'Планка торцевая'),
    productSealer: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Доборные элементы')
      .filter((product: IProduct) => product.coating === roofStore.coatingName)
      .find((product: IProduct) => product.type === 'Уплотнитель 2 метра'),
    productScrew: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Саморезы')
      .filter((product: IProduct) => roofStore.coatingName === 'цинк' ? product.coating === 'цинк' : product.coating === 'полиэстер')
      .find((product: IProduct) => product.type === 'Саморез 4,8х28'),
    productScrew35: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Саморезы')
      .filter((product: IProduct) => roofStore.coatingName === 'цинк' ? product.coating === 'цинк' : product.coating === 'полиэстер')
      .find((product: IProduct) => product.type === 'Саморез 4,8х35'),
    productScrew70: appStore.priceList
      .filter((product: IProduct) => product.offer_group === 'Саморезы')
      .filter((product: IProduct) => roofStore.coatingName === 'цинк' ? product.coating === 'цинк' : product.coating === 'полиэстер')
      .find((product: IProduct) => product.type === 'Саморез 4,8х70'),
  };
};
